import React from "react";
import { getIconForUID } from "components/FormerEditor/utils";
import { STATE_KEYS } from "utils/constants/state";
import compact from "lodash/compact";
import flatten from "lodash/flatten";
import get from "lodash/get";
import startCase from "lodash/startCase";
import { ensureAppCheckout } from "utils/hooks";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { fetchAppCheckout } from "actions/app";
import {
  CAPTURE_METHOD,
  PAYMENT_METHOD_TYPE,
  PAYMENT_MODE,
  SETUP_FUTURE_USAGE
} from "utils/constants/payment";
import { PAYMENT_METHOD_TYPE_ICON_MAP } from "components/PaymentMethodIcons";
import { pluralizeCopy } from "utils/copy";

// alias: CheckoutSummaryDetails
const ShortLinkFulfillmentDetails = ({ data, actions }) => {
  const receiver = get(data, STATE_KEYS.SHORT_LINK.CUSTOMER_EMAIL);
  const attachments = get(data, STATE_KEYS.SHORT_LINK.ATTACHMENTS);
  const autoTaxes = get(data, STATE_KEYS.SHORT_LINK.AUTOMATIC_TAX_ENABLED);
  const confirmation = get(data, STATE_KEYS.SHORT_LINK.CONFIRMATION_DATA);
  const primaries = get(data, STATE_KEYS.SHORT_LINK.PRIMARIES) || [];
  const hasAdjustableQuantities = primaries.find(
    ({ adjustable_quantity }) =>
      adjustable_quantity && adjustable_quantity.enabled
  );

  const taxIdCollectionEnabled = get(
    data,
    STATE_KEYS.SHORT_LINK.TAX_ID_COLLECTION_ENABLED
  );
  const taxIdCollectionType = get(
    data,
    STATE_KEYS.SHORT_LINK.TAX_ID_COLLECTION_TYPE
  );

  const captureMethod = get(
    data,
    STATE_KEYS.SHORT_LINK.PAYMENT_INTENT_DATA_CAPTURE_METHOD
  );
  const preAuthorize = captureMethod === CAPTURE_METHOD.MANUAL;

  const setupFutureUsage = get(
    data,
    STATE_KEYS.SHORT_LINK.PAYMENT_INTENT_DATA_SETUP_FUTURE_USAGE
  );

  const allowPromotionCodes = get(
    data,
    STATE_KEYS.SHORT_LINK.ALLOW_PROMOTION_CODES
  );

  const mode = get(data, STATE_KEYS.SHORT_LINK.MODE);

  /**
   * Shipping settings
   */
  const shippingWorldwide = get(data, STATE_KEYS.SHORT_LINK.SHIPPING_WORLDWIDE);
  const shippingCountries = get(
    data,
    STATE_KEYS.SHORT_LINK.SHIPPING_ALLOWED_COUNTRIES
  );
  const shippingOptions = get(data, STATE_KEYS.SHORT_LINK.SHIPPING_OPTIONS);
  const shippingEnabled =
    shippingWorldwide || (shippingCountries && shippingCountries.length > 0);

  const paymentMethodTypes = get(
    data,
    STATE_KEYS.SHORT_LINK.PAYMENT_METHOD_TYPES
  );

  const { checkout } = ensureAppCheckout({
    fetchAppCheckout: actions.fetchAppCheckout
  });
  let countries = [];
  if (checkout) {
    countries = checkout.countries;
  }

  let nodes = [];
  if (shippingEnabled) {
    const Globe = getIconForUID("shipping");
    let copy;
    if (shippingWorldwide) {
      copy = "Shipping worldwide";
    } else if (countries) {
      const countriesCount = shippingCountries.length;
      const country = countries.find(
        ({ code }) => code === shippingCountries[0]
      );

      const shippingLabels = [`Shipping to ${country.name}`];
      if (countriesCount > 1) {
        const remCount = countriesCount - 1;
        shippingLabels.push(
          `and ${remCount} other ${remCount > 1 ? "countries" : "country"}`
        );
      }

      copy = `${shippingLabels.join(" ")}`;
    }

    if (Array.isArray(shippingOptions) && shippingOptions.length > 1) {
      copy += " with multiple options";
    }
    copy += ".";

    nodes.push(
      <li
        className="w-100 flex flex-row justify-between items-center pt3"
        key="shipping"
      >
        <div className="f7 fw5 flex-shrink-0">{copy}</div>
        <Globe size={18} />
      </li>
    );
  }

  if (receiver) {
    nodes.push(
      <li
        className="w-100 flex flex-row justify-between items-center pt3"
        key="receiver"
      >
        <div className="f7 fw5 flex-shrink-0">Receiver</div>
        <div className="f7">{receiver}</div>
      </li>
    );
  }

  if (attachments && attachments.length) {
    nodes.push(
      <li
        className="w-100 flex flex-row justify-between items-center pt3"
        key="files"
      >
        <div className="f7 fw5 flex-shrink-0">Files</div>
        <div className="f7">{pluralizeCopy("file", attachments.length)}</div>
      </li>
    );
  }

  if (autoTaxes) {
    nodes.push(
      <li
        className="w-100 flex flex-row justify-between items-center pt3"
        key="autoTaxes"
      >
        <div className="f7 fw5 flex-shrink-0">Auto tax collection</div>
        <div className="f7">On</div>
      </li>
    );
  }

  if (taxIdCollectionEnabled) {
    nodes.push(
      <li
        className="w-100 flex flex-row justify-between items-center pt3"
        key="taxIdCollection"
      >
        <div className="f7 fw5 flex-shrink-0">Tax id collection</div>
        <div className="f7">{startCase(taxIdCollectionType)}</div>
      </li>
    );
  }

  if (preAuthorize) {
    nodes.push(
      <li
        className="w-100 flex flex-row justify-between items-center pt3"
        key="preAuthorize"
      >
        <div className="f7 fw5 flex-shrink-0">Authorize payment only</div>
        <div className="f7">On</div>
      </li>
    );
  }

  if (setupFutureUsage) {
    nodes.push(
      <li
        className="w-100 flex flex-row justify-between items-center pt3"
        key="offSession"
      >
        <div className="f7 fw5 flex-shrink-0">Save payment method</div>
        <div className="f7">{startCase(setupFutureUsage)}</div>
      </li>
    );
  }

  if (allowPromotionCodes) {
    nodes.push(
      <li
        className="w-100 flex flex-row justify-between items-center pt3"
        key="allowPromotionCodes"
      >
        <div className="f7 fw5 flex-shrink-0">Allow promotion codes</div>
        <div className="f7">Yes</div>
      </li>
    );
  }

  if (confirmation && confirmation.copy) {
    nodes.push(
      <li
        className="w-100 flex flex-row justify-between items-center pt3"
        key="customConfirmation"
      >
        <div className="f7 fw5 flex-shrink-0">Confirmation message</div>
        <div className="f7">Yes</div>
      </li>
    );
  }

  if (hasAdjustableQuantities) {
    nodes.push(
      <li
        className="w-100 flex flex-row justify-between items-center pt3"
        key="hasAdjustableQuantities"
      >
        <div className="f7 fw5 flex-shrink-0">Adjustable quantity</div>
        <div className="f7">Yes</div>
      </li>
    );
  }

  const allPaymentMethodTypes = compact(flatten([paymentMethodTypes]));
  if (mode !== PAYMENT_MODE.SETUP && !autoTaxes) {
    allPaymentMethodTypes.push(PAYMENT_METHOD_TYPE.APPLE_PAY);
    allPaymentMethodTypes.push(PAYMENT_METHOD_TYPE.GOOGLE_PAY);
  }

  if (allPaymentMethodTypes && allPaymentMethodTypes.length) {
    nodes.push(
      <li className="w-100 flex flex-column pt3" key="payment-methods">
        <div className="f7 fw5 flex-shrink-0">Payment methods</div>
        <div className="flex flex-grid w-100 pt2">
          {allPaymentMethodTypes.reduce((memo, type) => {
            const TypeComp = PAYMENT_METHOD_TYPE_ICON_MAP[type];
            if (TypeComp) {
              memo.push(
                <div className="pa1 flex items-center" key={type}>
                  <TypeComp />
                </div>
              );
            }

            return memo;
          }, [])}
        </div>
      </li>
    );
  }

  return <ul className="ma0 pa0 list">{nodes}</ul>;
};

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      fetchAppCheckout
    },
    dispatch
  )
});

export default connect(null, mapDispatchToProps)(ShortLinkFulfillmentDetails);
